import { initializeApp } from 'firebase/app';
import { getFirestore } from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import phoneCountries from './phoneCountries';
import {
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  signInWithRedirect,
  signInWithPhoneNumber,
  signInWithPopup,
} from 'firebase/auth';
import { getAnalytics, logEvent } from 'firebase/analytics';
import { DEV } from './constants';

export let app = initializeApp({
  apiKey: process.env.NEXT_PUBLIC_apiKey,
  authDomain: process.env.NEXT_PUBLIC_authDomain, // "fantasyoscars.siteless.co"
  databaseURL: process.env.NEXT_PUBLIC_databaseURL,
  projectId: process.env.NEXT_PUBLIC_projectId,
  storageBucket: process.env.NEXT_PUBLIC_storageBucket,
  messagingSenderId: process.env.NEXT_PUBLIC_messagingSenderId,
  appId: process.env.NEXT_PUBLIC_appId,
  measurementId: process.env.NEXT_PUBLIC_measurementId,
});

export let functions = getFunctions(app);
export let auth = getAuth(app);
auth.useDeviceLanguage();

export type analyticsEvents = {
  linkVisit: {
    key: 'linkVisit';
    payload: { currentUserId?: string; linkId: string };
  };
  pageLoad: {
    key: 'pageLoad';
    payload: undefined;
  };
};

export let logAnalyticsEvent = (
  event: analyticsEvents[keyof analyticsEvents],
) => {
  if (typeof window === 'undefined') return null;
  // this must run only on the client
  let analytics = getAnalytics(app);
  let prefix = DEV ? 'DEV__' : '';
  logEvent(analytics, prefix + event.key, event.payload);
};

export let database = getFirestore(app);

let googleAuthProvider = new GoogleAuthProvider();

let AUTH_REDIRECT_PARAM = 'authRedirect';

let redirectSignInFailed = false;
export let markRedirectSignInFailed = () => {
  redirectSignInFailed = true;
};

// Resolve a pending redirect sign-in. Returns true if the redirect produced
// a signed-in user, false if it silently failed or threw.
export let resolveGoogleAuthRedirect = async (): Promise<boolean> => {
  try {
    let result = await getRedirectResult(auth);
    return Boolean(result?.user);
  } catch {
    return false;
  }
};

export let startGoogleLoginPopup = () =>
  signInWithPopup(auth, googleAuthProvider);

export let hasAuthRedirectParam = () => {
  if (typeof window === 'undefined') return false;
  return new URL(window.location.href).searchParams.has(AUTH_REDIRECT_PARAM);
};

export let cleanAuthRedirectParam = () => {
  let url = new URL(window.location.href);
  url.searchParams.delete(AUTH_REDIRECT_PARAM);
  window.history.replaceState(null, '', url.toString());
};

export let startGoogleLogin = async () => {
  try {
    if (DEV || redirectSignInFailed)
      await signInWithPopup(auth, googleAuthProvider);
    else {
      let url = new URL(window.location.href);
      url.searchParams.set(AUTH_REDIRECT_PARAM, '1');
      window.history.replaceState(null, '', url.toString());
      // see https://firebase.google.com/docs/auth/web/redirect-best-practices?hl=en
      await signInWithRedirect(auth, googleAuthProvider);
    }
  } catch (error) {
    window.alert(error);
  }
};

export let startPhoneLogin = async ({
  phoneCountryKey,
  phoneNumber,
  recaptchaVerifier,
}: {
  phoneCountryKey: keyof typeof phoneCountries;
  phoneNumber: string;
  recaptchaVerifier: any;
}) => {
  try {
    let confirmer = await signInWithPhoneNumber(
      auth,
      '+' + phoneCountries[phoneCountryKey].dialCode + phoneNumber,
      recaptchaVerifier,
    );
    return confirmer;
  } catch (error) {
    window.alert(error);
  }
};

